/**
 * Matrix client bootstrap. Rooms are UNENCRYPTED, so we deliberately create
 * the client WITHOUT any crypto init (no initCrypto / initRustCrypto).
 */
import { createClient, ClientEvent, SyncState, type MatrixClient } from "matrix-js-sdk";
import type { SessionExchangeData } from "../api/types";

/** Create a Matrix client from exchanged session credentials. No crypto. */
export function createMatrixClient(session: SessionExchangeData): MatrixClient {
  return createClient({
    baseUrl: session.homeserverUrl,
    accessToken: session.accessToken,
    userId: session.userId,
  });
}

export interface StartOptions {
  initialSyncLimit?: number;
  timeoutMs?: number;
}

/**
 * Start the client and resolve once the first sync reaches PREPARED.
 * Rejects on sync ERROR before PREPARED, or after `timeoutMs`.
 */
export function startClientAndWaitForSync(
  client: MatrixClient,
  { initialSyncLimit = 30, timeoutMs = 30_000 }: StartOptions = {},
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      client.removeListener(ClientEvent.Sync, onSync);
      if (timer) clearTimeout(timer);
    };

    const onSync = (state: SyncState) => {
      if (settled) return;
      if (state === SyncState.Prepared) {
        settled = true;
        cleanup();
        resolve();
      } else if (state === SyncState.Error) {
        settled = true;
        cleanup();
        reject(new Error("Initial sync failed. The session may be invalid or the server unreachable."));
      }
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("Timed out waiting for the chat server to sync."));
    }, timeoutMs);

    client.on(ClientEvent.Sync, onSync);

    client.startClient({ initialSyncLimit }).catch((err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error("Failed to start Matrix client."));
    });
  });
}

/** Stop the client and release listeners. Safe to call multiple times. */
export function stopMatrixClient(client: MatrixClient): void {
  try {
    client.stopClient();
  } catch {
    // ignore — client may already be stopped
  }
}
